import { Dialog as BaseDialog } from '@base-ui/react/dialog'
import { X } from 'lucide-react'
import { type ReactNode, useState } from 'react'

import { useScrollEdges } from '../../../hooks/use-scroll-edges.ts'
import { cn } from '../../../lib/utils.ts'
import { Button } from '../../ui/button/button.tsx'
import { Dialog as DialogPrimitive } from '../../ui/dialog/dialog.tsx'

import { DialogOpenContext } from './dialog-open-context.ts'

/**
 * `Dialog` is the assembled dialog: `Dialog.Root` + `Dialog.Trigger` + `Dialog.Popup`, with the layout
 * parts `Header` (with `Title` and `CloseIconButton`), `Body`, `Description`, `Footer` and `Close`.
 *
 * `Popup` pre-wires Portal + Backdrop + Popup, which carry no decision. `Header`, `Body` and `Footer` rely
 * on it to coordinate adjacent padding: render them as direct children of `Popup`, through a fragment if
 * they come from another component. When composing `ui/dialog` directly, provide your own layout.
 *
 * - **Uncontrolled** (`defaultOpen`, optional `onOpenChange`): a `Trigger` opens it, any `Close` part, Escape
 *   or the backdrop closes it. Use it when nothing outside the dialog has to close it.
 * - **Controlled** (`open` + required `onOpenChange`): use it when the dialog closes after an async action, or opens
 *   from something other than a `Trigger`. The callback receives every requested open-state change.
 * - Focus returns to the `Trigger` on close, in both modes.
 * - `useDialogState` and `useDialogAction` read the open state of the nearest `Root`, so call them in a
 *   component rendered under it. They throw outside one.
 * - `Body` is the only scrollable part; `Header` and `Footer` do not shrink. It marks its scrolled
 *   edges with `data-scrolled-top` / `data-scrolled-bottom`, styled as dividers so scrolled-past
 *   content doesn't read as clipped under the Header/Footer.
 * - `Close` is the headless close action from Base UI, for turning your own element into a close
 *   affordance (`render={<Button>Cancel</Button>}`). `CloseIconButton` is the ready-made ✕.
 * - For triggers detached from the Root (`createHandle`), compose `ui/dialog` directly.
 *
 * @summary Assembled dialog (root / trigger / popup / header / body / footer / close), controlled or not.
 */
function DialogRoot(props: DialogRoot.Props) {
  const { open, defaultOpen = false, onOpenChange, children } = props
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
  const resolvedOpen = open ?? uncontrolledOpen

  const handleOpenChange = (nextOpen: boolean): void => {
    if (open === undefined) setUncontrolledOpen(nextOpen)
    onOpenChange?.(nextOpen)
  }

  return (
    <DialogPrimitive.Root open={resolvedOpen} onOpenChange={handleOpenChange}>
      <DialogOpenContext value={resolvedOpen}>{children}</DialogOpenContext>
    </DialogPrimitive.Root>
  )
}
namespace DialogRoot {
  type ControlledProps = {
    open: boolean
    /** Called whenever the dialog asks to open or close. */
    onOpenChange: (open: boolean) => void
    defaultOpen?: never
  }
  type UncontrolledProps = {
    open?: never
    /** Open on mount. @default false */
    defaultOpen?: boolean
    /** Called whenever the dialog asks to open or close. */
    onOpenChange?: (open: boolean) => void
  }
  export type Props = { children: ReactNode } & (ControlledProps | UncontrolledProps)
}

function DialogTrigger(props: DialogTrigger.Props) {
  return <DialogPrimitive.Trigger {...props} />
}
namespace DialogTrigger {
  export type Props = DialogPrimitive.Trigger.Props
}

/** Portal + Backdrop + Popup, with the padding coordination between `Header`, `Body` and `Footer`. */
function DialogPopup(props: DialogPopup.Props) {
  const { size, blur = false, children } = props
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Backdrop blur={blur} />
      <DialogPrimitive.Popup
        size={size}
        className={cn(
          `
            [&>[data-slot='dialog-body']+[data-slot='dialog-footer']]:pt-[calc(var(--spacing-card)-var(--spacing-10))]
            [&>[data-slot='dialog-body']:has(+[data-slot='dialog-footer'])]:pb-10
            [&>[data-slot='dialog-header']+[data-slot='dialog-body']]:pt-10
            [&>[data-slot='dialog-header']:has(+[data-slot='dialog-body'])]:pb-[calc(var(--spacing-card)-var(--spacing-10))]
          `,
        )}
      >
        {children}
      </DialogPrimitive.Popup>
    </DialogPrimitive.Portal>
  )
}
namespace DialogPopup {
  export type Props = {
    /** Max width on desktop. Auto-collapses to fullscreen below the `md` breakpoint. @default 'medium' */
    size?: DialogPrimitive.Popup.Props['size']
    /** Apply a 12 px backdrop blur to the page behind the dialog. @default false */
    blur?: boolean
    children: ReactNode
  }
}

function DialogHeader(props: DialogHeader.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex shrink-0 items-center justify-between gap-50 p-card', className)}
      {...rest}
    />
  )
}
namespace DialogHeader {
  export type Props = React.ComponentProps<'div'>
}

function DialogTitle(props: DialogTitle.Props) {
  const { className, ...rest } = props
  return <DialogPrimitive.Title className={cn('flex-1', className)} {...rest} />
}
namespace DialogTitle {
  export type Props = DialogPrimitive.Title.Props
}

/**
 * The ✕ renders as a bordered `secondary` icon button because the design system's dialog header
 * specifies that look for every dialog, so don't revert it to `variant="text"` for a single screen.
 */
function DialogCloseIconButton(props: DialogCloseIconButton.Props) {
  const { className, ...rest } = props
  return (
    <BaseDialog.Close
      data-slot="dialog-close-icon-button"
      render={<Button variant="secondary" size="icon-small" className={className} />}
      {...rest}
    >
      <X />
    </BaseDialog.Close>
  )
}
namespace DialogCloseIconButton {
  export type Props = Omit<BaseDialog.Close.Props, 'children' | 'render'> & {
    /** Required: the button is icon-only, so it has no text to name it. */
    'aria-label': string
  }
}

function DialogBody(props: DialogBody.Props) {
  const { className, ...rest } = props
  const { ref, scrollable, scrolledTop, scrolledBottom } = useScrollEdges<HTMLDivElement>()
  return (
    <div
      data-slot="dialog-body"
      ref={ref}
      // Chrome and Safari never focus a scroll container. Without a tab stop, a Body holding no
      // focusable content cannot be scrolled by keyboard at all. A Body that fits needs no stop.
      tabIndex={scrollable ? 0 : undefined}
      data-scrolled-top={scrolledTop || undefined}
      data-scrolled-bottom={scrolledBottom || undefined}
      className={cn(
        `
          flex min-h-0 flex-1 flex-col gap-70 overflow-y-auto border-line-base-basic px-card py-card
          focus-visible:outline-3 focus-visible:-outline-offset-3 focus-visible:outline-line-base-input-focus/50
          focus-visible:outline-solid
          data-[scrolled-bottom]:border-b
          data-[scrolled-top]:border-t
          max-md:gap-60
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace DialogBody {
  // `ref` is owned by `useScrollEdges` — the hook needs it to track scroll/resize/mutation, so a
  // consumer-supplied `ref` here would silently replace it and stop the dividers from updating.
  export type Props = Omit<React.ComponentProps<'div'>, 'ref'>
}

function DialogFooter(props: DialogFooter.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        `
          flex shrink-0 items-center justify-end gap-inline p-card
          max-md:flex-col max-md:pb-[calc(var(--spacing-card)+env(safe-area-inset-bottom))]
          max-md:[&>[data-slot='button']]:w-full
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace DialogFooter {
  export type Props = React.ComponentProps<'div'>
}

const Dialog = {
  Root: DialogRoot,
  Trigger: DialogTrigger,
  Popup: DialogPopup,
  Header: DialogHeader,
  Title: DialogTitle,
  CloseIconButton: DialogCloseIconButton,
  Body: DialogBody,
  Description: DialogPrimitive.Description,
  Footer: DialogFooter,
  Close: DialogPrimitive.Close,
}

namespace Dialog {
  export namespace Root {
    export type Props = DialogRoot.Props
  }
  export namespace Trigger {
    export type Props = DialogTrigger.Props
  }
  export namespace Popup {
    export type Props = DialogPopup.Props
  }
  export namespace Header {
    export type Props = DialogHeader.Props
  }
  export namespace Title {
    export type Props = DialogTitle.Props
  }
  export namespace CloseIconButton {
    export type Props = DialogCloseIconButton.Props
  }
  export namespace Body {
    export type Props = DialogBody.Props
  }
  export namespace Description {
    export type Props = DialogPrimitive.Description.Props
  }
  export namespace Footer {
    export type Props = DialogFooter.Props
  }
  export namespace Close {
    export type Props = DialogPrimitive.Close.Props
  }
}

export { Dialog }
export { useDialogAction } from './use-dialog-action.ts'
export { useDialogState } from './use-dialog-state.ts'
